import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { FileText, AlertCircle, Scale, Package, CreditCard, ShieldCheck } from 'lucide-react';
import SEO from '../components/SEO';

const Terms = () => {
  const sections = [
    {
      icon: <Scale className="h-6 w-6 text-[#7bb5d4]" />,
      title: 'Acceptance of Terms',
      content: `By accessing and using the Kalorama Medical website and services, you accept and agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our website or services. We reserve the right to modify these terms at any time, and such modifications will be effective immediately upon posting.`
    },
    {
      icon: <Package className="h-6 w-6 text-[#7bb5d4]" />,
      title: 'Products and Services',
      content: `Kalorama Medical provides durable medical equipment and related services. All products are subject to availability. We reserve the right to discontinue any product at any time. Product descriptions and images are provided for informational purposes and may not reflect exact specifications. Consult with a healthcare professional before purchasing medical equipment.`
    },
    {
      icon: <CreditCard className="h-6 w-6 text-[#7bb5d4]" />,
      title: 'Pricing and Payment',
      content: `Prices for products and services are subject to change without notice. We work with Medicare, Medicaid, and most major insurance providers. Insurance coverage varies by plan and product. You are responsible for any costs not covered by your insurance. Payment terms will be discussed during the consultation process.`
    },
    {
      icon: <ShieldCheck className="h-6 w-6 text-[#7bb5d4]" />,
      title: 'Warranty and Returns',
      content: `Products are covered by manufacturer warranties where applicable. Warranty terms vary by product and manufacturer. Returns may be accepted within 30 days of delivery for unused items in original packaging. Certain medical equipment may not be eligible for return due to health and safety regulations. Contact us for specific return policies.`
    },
    {
      icon: <AlertCircle className="h-6 w-6 text-[#7bb5d4]" />,
      title: 'Limitation of Liability',
      content: `Kalorama Medical shall not be liable for any indirect, incidental, special, or consequential damages arising from the use of our products or services. Our liability is limited to the purchase price of the product. We do not provide medical advice; consult with healthcare professionals for medical decisions. Product use is at the customer's own risk.`
    },
    {
      icon: <FileText className="h-6 w-6 text-[#7bb5d4]" />,
      title: 'Intellectual Property',
      content: `All content on this website, including text, graphics, logos, and images, is the property of Kalorama Medical or its content suppliers and is protected by copyright laws. You may not reproduce, distribute, or create derivative works without our express written permission. The Kalorama Medical name and logo are trademarks of our company.`
    }
  ];

  const additionalTerms = [
    {
      title: 'User Conduct',
      points: [
        'Use our website only for lawful purposes',
        'Do not attempt to gain unauthorized access to our systems',
        'Do not transmit any harmful or malicious content',
        'Respect the privacy of other users'
      ]
    },
    {
      title: 'Governing Law',
      points: [
        'These terms are governed by applicable state and federal laws',
        'Any disputes shall be resolved through appropriate legal channels',
        'You agree to submit to the jurisdiction of our local courts',
        'Invalid provisions shall not affect the validity of remaining terms'
      ]
    }
  ];

  return (
    <div className="min-h-screen" data-testid="terms-page">
      <SEO 
        title="Terms of Service | Kalorama Medical"
        description="Read the terms and conditions for using Kalorama Medical's website and services. Understand your rights and responsibilities as a customer."
        keywords="terms of service, terms and conditions, user agreement, medical equipment terms"
      />
      
      {/* Hero */}
      <div className="bg-gradient-to-br from-[#1e3a5f] to-[#2c5282] text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <FileText className="h-16 w-16 mx-auto mb-6 text-[#7bb5d4]" />
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Terms of Service</h1>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto">
            Please read these terms carefully before using our website and services.
          </p>
        </div>
      </div>

      {/* Last Updated */}
      <div className="bg-blue-50 py-4">
        <div className="container mx-auto px-4 text-center">
          <p className="text-gray-600 text-sm">Last Updated: February 2026</p>
        </div>
      </div>

      {/* Content */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Main Sections */}
            <div className="space-y-6">
              {sections.map((section, index) => (
                <Card key={index} className="border-2 border-blue-100 hover:border-[#7bb5d4] transition-all duration-300">
                  <CardContent className="p-8">
                    <div className="flex items-center mb-4">
                      <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center mr-4">
                        {section.icon}
                      </div>
                      <h2 className="text-2xl font-bold text-[#1e3a5f]">{section.title}</h2>
                    </div>
                    <p className="text-gray-700 leading-relaxed">{section.content}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Additional Terms */}
            <div className="mt-8 grid md:grid-cols-2 gap-6">
              {additionalTerms.map((term, index) => (
                <Card key={index} className="border-2 border-blue-100">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-semibold text-[#1e3a5f] mb-4">{term.title}</h3>
                    <ul className="space-y-2">
                      {term.points.map((point, idx) => (
                        <li key={idx} className="flex items-start text-gray-700 text-sm">
                          <span className="text-[#7bb5d4] mr-2 mt-1">&#10003;</span>
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Agreement Notice */}
            <Card className="mt-8 bg-blue-50 border-2 border-[#7bb5d4]">
              <CardContent className="p-6 text-center">
                <AlertCircle className="h-8 w-8 mx-auto mb-3 text-[#1e3a5f]" />
                <p className="text-gray-700">
                  By using our website or services, you acknowledge that you have read, understood, and agree 
                  to be bound by these Terms of Service. If you have any questions, please contact us before proceeding.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Terms;
